/**
 * The navigation chrome's inline SVG icon set: one mark per destination
 * workspace, El Parte's, the Home/Board local views, «Más» and the fold
 * toggle. Paths are v20's; strokes and size come from `.app-navigation__mark`.
 */
import type { PanelDestination, PanelDestinationId } from './access';

export type DestinationIconPart =
  | string
  | readonly ['circle', number, number, number]
  | readonly ['rect', number, number, number, number, number];

export const DESTINATION_ICON_PARTS: Readonly<Record<PanelDestinationId, readonly DestinationIconPart[]>> = {
  CONSTRUCTOR: [['rect', 14, 3, 7, 7, 1], 'M10 21V8a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-5a1 1 0 0 0-1-1H3'],
  ANALYTICS: ['M3 3v16a2 2 0 0 0 2 2h16', 'm19 9-5 5-4-4-3 3'],
  MARKETING: ['m3 11 18-5v12L3 14v-3z', 'M11.6 16.8a3 3 0 1 1-5.8-1.6'],
  LOYALTY: [['rect', 2, 5, 20, 14, 2], 'M2 10h20', 'M6 15h4'],
  INVENTORY: ['m7.5 4.27 9 5.15', 'M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z', 'M3.3 7 12 12l8.7-5', 'M12 22V12'],
  CUSTOMER_HISTORY: ['M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8', 'M3 3v5h5', 'M12 7v5l4 2'],
  HR: ['M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2', ['circle', 9, 7, 4], 'M22 21v-2a4 4 0 0 0-3-3.87', 'M16 3.13a4 4 0 0 1 0 7.75'],
  MENU: ['M12 7v14', 'M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z'],
  SERVICE: ['M3 20a1 1 0 0 1-1-1v-1a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v1a1 1 0 0 1-1 1Z', 'M20 16a8 8 0 1 0-16 0', 'M12 4v4', 'M10 4h4'],
  KITCHEN: ['M17 21a1 1 0 0 0 1-1v-5.35c0-.457.316-.844.727-1.041a4 4 0 0 0-2.134-7.589 5 5 0 0 0-9.186 0 4 4 0 0 0-2.134 7.588c.411.198.727.585.727 1.041V20a1 1 0 0 0 1 1Z', 'M6 17h12'],
  POS: [['rect', 2, 5, 20, 14, 2], 'M2 10h20'],
};

/**
 * v20 `deptIcons().parte`. El Parte is a local view, not a workspace, so its
 * mark cannot live in the destination table keyed by `PanelDestinationId`.
 */
export const PARTE_ICON_PARTS: readonly DestinationIconPart[] = [
  ['circle', 12, 12, 4], 'M12 2v2', 'M12 20v2', 'm4.9 4.9 1.4 1.4', 'm17.7 17.7 1.4 1.4',
  'M2 12h2', 'M20 12h2', 'm6.3 17.7-1.4 1.4', 'm19.1 4.9-1.4 1.4',
];

export const NavigationMark = ({ parts }: { readonly parts: readonly DestinationIconPart[] }) => (
  <svg aria-hidden="true" className="app-navigation__mark" fill="none" focusable="false" viewBox="0 0 24 24">
    {parts.map((part, index) => {
      if (typeof part === 'string') return <path d={part} key={index} />;
      if (part[0] === 'circle') return <circle cx={part[1]} cy={part[2]} key={index} r={part[3]} />;
      return <rect height={part[4]} key={index} rx={part[5]} width={part[3]} x={part[1]} y={part[2]} />;
    })}
  </svg>
);

export const DestinationMark = ({ destination }: { readonly destination: PanelDestination }) => (
  <NavigationMark parts={DESTINATION_ICON_PARTS[destination.workspaceId]} />
);

export function LocalViewMark({ id }: { readonly id: 'home' | 'board' }) {
  return <svg aria-hidden="true" className="app-navigation__mark" fill="none" focusable="false" viewBox="0 0 24 24">
    {id === 'home' ? <path d="M3 10.5 12 3l9 7.5M5 9.5V21h14V9.5" /> : <><rect height="7" rx="1" width="7" x="3" y="3" /><rect height="7" rx="1" width="7" x="14" y="3" /><rect height="7" rx="1" width="7" x="3" y="14" /><rect height="7" rx="1" width="7" x="14" y="14" /></>}
  </svg>;
}

export function MoreMark() {
  return <svg aria-hidden="true" className="app-navigation__mark" fill="none" focusable="false" viewBox="0 0 24 24">
    <circle cx="5" cy="12" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="19" cy="12" r="1.6" />
  </svg>;
}

export function NavigationToggleMark() {
  return <svg aria-hidden="true" fill="none" height="18" viewBox="0 0 24 24" width="18">
    <rect height="16" rx="2" width="18" x="3" y="4" /><path d="M9 4v16" />
  </svg>;
}
